import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import './styles/accessibility.css'; // WCAG 2.1 AA compliance styles
import './bootWatchdog';
import App from './App.tsx';
import { Diagnostics } from './utils/diagnostics';
// Initialize error log utilities
import './utils/consoleErrorDump';

// Initialize Monitoring Service (optional - fails gracefully)
// Only initialize if Sentry DSN is available
// Note: MonitoringService is also initialized in App.tsx with user context,
// so we skip initialization here to avoid double initialization
// REMOVED: initSentry() call - Sentry is now initialized only in App.tsx via MonitoringService
// to avoid "Multiple Sentry Session Replay instances" error
try {
  const sentryDsn = import.meta.env?.VITE_SENTRY_DSN;
  if (sentryDsn) {
    // Only log that Sentry is configured - actual initialization happens in App.tsx
    // with user context for better error tracking
    // eslint-disable-next-line no-console
    console.debug(
      '[Cyclone] Sentry DSN configured, will initialize in App component'
    );
  }
} catch (_error) {
  // Silently fail - monitoring is optional
  // eslint-disable-next-line no-console
  console.debug('[Cyclone] Sentry not configured');
}

// Run diagnostics in development mode
if (import.meta.env.DEV) {
  Diagnostics.runAllChecks()
    .then(results => {
      Diagnostics.displayResults(results);
    })
    .catch(error => {
      console.warn('[Cyclone] Diagnostics failed:', error);
    });
}

// Initialize Error Tracking
import('./services/monitoring/errorTrackingService')
  .then(({ ErrorTrackingService }) => {
    ErrorTrackingService.initializeGlobalHandlers();
  })
  .catch(() => {
    // Fallback error handlers if ErrorTrackingService not available
    window.addEventListener('error', e => {
      const errorMessage = e.message || '';
      const errorSource = e.filename || '';

      // Ignore chrome extension errors
      if (
        errorSource.includes('chrome-extension://') ||
        errorSource.includes('moz-extension://') ||
        errorMessage.includes('message channel closed') ||
        errorMessage.includes('Extension context invalidated')
      ) {
        return;
      }

      console.error('[Cyclone] Error:', e.error || e.message);
    });

    window.addEventListener('unhandledrejection', e => {
      const reason = e.reason;
      const errorMessage =
        reason instanceof Error ? reason.message : String(reason);

      // Filter out chrome extension errors
      if (
        errorMessage.includes('message channel closed') ||
        errorMessage.includes('Extension context invalidated') ||
        errorMessage.includes('A listener indicated an asynchronous response')
      ) {
        return;
      }

      console.error('[Cyclone] Unhandled rejection:', e.reason);
    });
  });

// Ensure Vite runtime is initialized before any chunks can load
// This prevents TDZ errors with Vite import helpers ('di', 'oA', etc.)
// Initialize in async IIFE to ensure runtime is ready
(async () => {
  // CRITICAL: Wait for Vite runtime to fully initialize before any chunks can load
  // browser-fs-access uses 'oA' helper at module level, which requires runtime to be ready
  // We need to ensure the main entry chunk has fully executed and initialized helpers
  
  // Wait for multiple event loop ticks to ensure Vite runtime helpers are fully initialized
  // This is critical for browser-fs-access which uses 'oA' helper at module level
  await new Promise(resolve => setTimeout(resolve, 0));
  await new Promise(resolve => setTimeout(resolve, 0));
  await new Promise(resolve => setTimeout(resolve, 0));
  await new Promise(resolve => setTimeout(resolve, 50));
  
  // Ensure document is ready
  if (document.readyState === 'loading') {
    await new Promise(resolve => document.addEventListener('DOMContentLoaded', resolve));
  }
  
  // Additional delays to ensure Vite runtime is fully ready
  // Multiple ticks ensure all runtime helpers are initialized
  await new Promise(resolve => setTimeout(resolve, 100));
  await new Promise(resolve => setTimeout(resolve, 50));
  
  // Mark that Vite runtime is ready (for lazy chunks to check)
  (window as any).__VITE_RUNTIME_READY__ = true;
  
  // Additional safety: ensure runtime helpers are actually available
  // This helps catch cases where runtime isn't fully initialized
  if (typeof (window as any).__vite_plugin_react_preamble_installed__ === 'undefined') {
    // Wait a bit more if React preamble isn't installed yet
    await new Promise(resolve => setTimeout(resolve, 50));
  }
  
  // Bootstrap React app
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('Root element not found');
  }

  const root = createRoot(rootEl);
  root.render(
    <StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </StrictMode>
  );

  // Mark app as mounted
  (window as any).__CYCLONE_MOUNTED__ = true;
  if ((window as any).__CYCLONE_BOOT_WATCHDOG__) {
    clearTimeout((window as any).__CYCLONE_BOOT_WATCHDOG__);
  }
})();
